import { useState, useEffect } from "react";
import { Plus, Info, Pause, RefreshCw, Trash2, XCircle, PlayCircle } from "react-feather";
import { Modal } from "../../../../components/Modal";
import { Colors } from "../../../../values/colors";
import {
  ModalTitle,
  TitleIcon,
  InfoBtn,
  TooltipWrapper,
  TooltipBox,
  FieldLabel,
  TextInput,
  DateRow,
  FieldGroup,
  StatusRow,
  StatusLabel,
  StatusBadge,
  ActionButton,
  Divider,
  FooterRow,
  CancelBtn,
  ConfirmBtn,
  ConfirmBody,
  ConfirmIcon,
  ConfirmText,
} from "./styles";
import { Text } from "../../../../components/BaseComponents/styles";

const STATUS_BG = {
  "inProgress": {
    label: "Em andamento",
    color: Colors.orange
  },
  "finalized": {
    label: "Finalizada",
    color: Colors.secondaryGreen
  },
  "stopped": {
    label: "Pausada",
    color: Colors.gray
  },
  "closed": {
    label: "Fechada",
    color: Colors.darkBlue
  },
  "scheduled": {
    label: "Agendada",
    color: Colors.primaryPurple
  }
};

const toDateTime = (dateStr) => (dateStr ? `${dateStr} 00:00:00` : dateStr);

// ─── Shared title renderer ────────────────────────────────────────────────────
const makeTitle = (icon, text, tooltip) => (
  <ModalTitle>
    <TitleIcon>{icon}</TitleIcon>
    <Text size={16} weight={700}>
      {text}
    </Text>
    <TooltipWrapper>
      <InfoBtn tabIndex={-1}>
        <Info size={14} />
      </InfoBtn>
      {tooltip && <TooltipBox>{tooltip}</TooltipBox>}
    </TooltipWrapper>
  </ModalTitle>
);

// ─── Create / Nova Pesquisa ───────────────────────────────────────────────────
const CreateContent = ({ onClose, onCreate }) => {
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const canSubmit = title.trim() !== "" && startDate !== "" && endDate !== "";

  const handleSubmit = () => {
    if (!canSubmit) return;
    onCreate({title, start_at: toDateTime(startDate), end_at: toDateTime(endDate)});
    onClose();
  };

  return (
    <>
      <FieldGroup>
        <FieldLabel>Título da nova pesquisa</FieldLabel>
        <TextInput
          placeholder=""
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </FieldGroup>

      <DateRow>
        <FieldGroup>
          <FieldLabel>Data de início</FieldLabel>
          <TextInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>Data de fim</FieldLabel>
          <TextInput
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </FieldGroup>
      </DateRow>

      <FooterRow>
        <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
        <ConfirmBtn
          bg={Colors.primary}
          disabled={!canSubmit}
          onClick={handleSubmit}>
          Criar Pesquisa
        </ConfirmBtn>
      </FooterRow>
    </>
  );
};

// ─── Edit / Controle do Questionário ─────────────────────────────────────────
const EditContent = ({ survey, onClose, onSave, onRequestPause, onRequestReactivate }) => {
  const [title, setTitle] = useState(survey?.title ?? "");
  const [startDate, setStartDate] = useState((survey?.start_at ?? "").split("T")[0]);
  const [endDate, setEndDate] = useState((survey?.end_at ?? "").split("T")[0]);
  const currentDate = new Date().toISOString().split("T")[0];
  
  const isOngoing = survey?.status === "inProgress";
  const isStatusHandlerVisibel = survey?.status === "scheduled" 
  || survey?.status === "finalized" 
  || survey?.status === "closed";
  const isDirty =
    title !== (survey?.title ?? "") ||
    startDate !== (survey?.start_at ?? "") ||
    endDate !== (survey?.end_at ?? "");

  const handleSave = () => {
    if (!isDirty) return;
    onSave({ ...survey, title, start_at: toDateTime(startDate), end_at: toDateTime(endDate) });
    onClose();
  };

  console.log(title, isOngoing, currentDate >= startDate);
  return (
    <>
      {!isStatusHandlerVisibel && (
        <>        
          <StatusRow>
            <StatusLabel>Status do Questionário</StatusLabel>
            <StatusBadge bg={STATUS_BG[survey?.status].color || Colors.lightGray}>
              {STATUS_BG[survey?.status]?.label}
            </StatusBadge>
          </StatusRow>
          <ActionButton
            bg={isOngoing ? Colors.primary : Colors.secondaryGreen}
            onClick={isOngoing ? onRequestPause : onRequestReactivate}
          >
            {isOngoing ? (
              <>
                <Pause size={15} /> Pausar
              </>
            ) : (
              <>
                <PlayCircle size={15} /> Reativar Questionário
              </>
            )}
          </ActionButton>
          <Divider />
        </>    
      )}
      <FieldGroup>
        <FieldLabel>Título da pesquisa</FieldLabel>
        <TextInput
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </FieldGroup>

      { (survey?.status !== "Finalizada") && (
        <DateRow>
          <FieldGroup>
            <FieldLabel>Data de início</FieldLabel>
            <TextInput
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={isOngoing || currentDate >= startDate}
              />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Data de fim</FieldLabel>
            <TextInput
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={currentDate >= endDate}
            />
          </FieldGroup>
        </DateRow>
      )}

      <FooterRow>
        <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
        <ConfirmBtn bg={Colors.primary} disabled={!isDirty} onClick={handleSave}>
          Salvar
        </ConfirmBtn>
      </FooterRow>
    </>
  );
};

// ─── Confirm pause ────────────────────────────────────────────────────────────
const ConfirmPauseContent = ({ onClose, onConfirm }) => (
  <>
    <ConfirmBody>
      <ConfirmIcon color={Colors.primaryPurple}>
        <Pause size={22} /> Pausar Questionário?
      </ConfirmIcon>
      <ConfirmText>Você tem certeza que deseja pausar o questionário?</ConfirmText>
    </ConfirmBody>
    <FooterRow>
      <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
      <ConfirmBtn bg={Colors.primary} onClick={onConfirm}>
        <Pause size={14} /> Pausar
      </ConfirmBtn>
    </FooterRow>
  </>
);

// ─── Confirm reactivate ───────────────────────────────────────────────────────
const ConfirmReactivateContent = ({ onClose, onConfirm }) => (
  <>
    <ConfirmBody>
      <ConfirmIcon color={Colors.secondaryGreen}>
        <PlayCircle size={22} /> Reativar Questionário?
      </ConfirmIcon>
      <ConfirmText>Você tem certeza que deseja reativar o questionário?</ConfirmText>
    </ConfirmBody>
    <FooterRow>
      <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
      <ConfirmBtn bg={Colors.secondaryGreen} onClick={onConfirm}>
        <PlayCircle size={14} /> Ativar
      </ConfirmBtn>
    </FooterRow>
  </>
);

// ─── Confirm delete ───────────────────────────────────────────────────────────
const ConfirmDeleteContent = ({ onClose, onConfirm }) => (
  <>
    <ConfirmBody>
      <ConfirmIcon color={Colors.red}>
        <Trash2 size={22} /> Deletar Pesquisa?
      </ConfirmIcon>
      <ConfirmText>Você tem certeza que deseja deletar esta pesquisa? Esta ação não pode ser desfeita.</ConfirmText>
    </ConfirmBody>
    <FooterRow>
      <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
      <ConfirmBtn bg={Colors.red} onClick={onConfirm}>
        <Trash2 size={14} /> Deletar
      </ConfirmBtn>
    </FooterRow>
  </>
);

// ─── Confirm encerrar ─────────────────────────────────────────────────────────
const ConfirmEncerrarContent = ({ onClose, onConfirm }) => (
  <>
    <ConfirmBody>
      <ConfirmIcon color={Colors.red}>
        <XCircle size={22} /> Encerrar Pesquisa?
      </ConfirmIcon>
      <ConfirmText>Você tem certeza que deseja encerrar esta pesquisa?</ConfirmText>
    </ConfirmBody>
    <FooterRow>
      <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
      <ConfirmBtn bg={Colors.red} onClick={onConfirm}>
        <XCircle size={14} /> Encerrar
      </ConfirmBtn>
    </FooterRow>
  </>
);

// ─── Modal config map ─────────────────────────────────────────────────────────
const MODAL_CONFIG = {
  create:            { title: makeTitle(<Plus size={16} />, "Nova Pesquisa", "Defina o título e o período da pesquisa para iniciar a coleta de respostas.") },
  edit:              { title: makeTitle(<Plus size={16} />, "Controle do Questionário", "Gerencie o status do questionário e atualize as informações da pesquisa, como título e período. ") },
  confirmPause:      { title: null },
  confirmReactivate: { title: null },
  confirmDelete:     { title: null },
  confirmEncerrar:   { title: null },
};

// ─── Main export ──────────────────────────────────────────────────────────────
export const SurveyModal = ({
  open,
  mode,
  survey,
  onClose,
  onCreate,
  onSave,
  onPause,
  onReactivate,
  onDelete,
  onEncerrar,
  onChangeMode,
}) => {
  const config = MODAL_CONFIG[mode] ?? {};

  const renderContent = () => {
    switch (mode) {
      case "create":
        return <CreateContent onClose={onClose} onCreate={onCreate} />;

      case "edit":
        return (
          <EditContent
            survey={survey}
            onClose={onClose}
            onSave={onSave}
            onRequestPause={() => onChangeMode("confirmPause")}
            onRequestReactivate={() => onChangeMode("confirmReactivate")}
          />
        );

      case "confirmPause":
        return (
          <ConfirmPauseContent
            onClose={onClose}
            onConfirm={() => { onPause(survey); onClose(); }}
          />
        );

      case "confirmReactivate":
        return (
          <ConfirmReactivateContent
            onClose={onClose}
            onConfirm={() => { onReactivate(survey); onClose(); }}
          />
        );

      case "confirmDelete":
        return (
          <ConfirmDeleteContent
            onClose={onClose}
            onConfirm={() => { onDelete(survey); onClose(); }}
          />
        );

      case "confirmEncerrar":
        return (
          <ConfirmEncerrarContent
            onClose={onClose}
            onConfirm={() => { onEncerrar(survey); onClose(); }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      title={config.title}
      onClose={onClose}
      disableHeader={!config.title}
    >
      {renderContent()}
    </Modal>
  );
};
